import React, { useEffect, useState, ChangeEvent, useRef } from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Row,
  Col,
} from 'reactstrap';
import { useUser } from 'context/UserContext';
import { useAlertPlus, useConfirm } from 'hooks';
import Required from 'components/Required';
import ConfirmModal from 'components/ConfirmModal';
import AlertModal from 'components/AlertModal';
import { useHistory } from 'react-router-dom';
import {
  postFormaPago,
} from '../../features/formapago/formapagoAPI';
import {
  FormaPagoRequest,
} from 'types/FormaPago';

export default function CreateSolicitudFormaPago() {
  const { getMatricula } = useUser();
  const matriculateId = getMatricula();
  if (!matriculateId) {
    return null;
  }

  const history = useHistory();

  const [formValidation, setFormValidation] = useState<
    Partial<Record<keyof FormaPagoRequest, boolean>>
  >({});

  const [isLoading, setLoading] = useState(false);

  const [showAlert, message, alertCallback, alert] = useAlertPlus('');
  const [confirm, confirmTitle, confirmAction, setConfirm] = useConfirm(false);

  const formElement = useRef<HTMLFormElement | null>(null);

  const [data, setData] = useState<FormaPagoRequest>({
    id: 0,
    state_id: null,
    matriculate_id: null,
    forma_pago: null,
    tipo_tarjeta: null,
    numero_tarjeta_cbu: null,
    created_at: null,
    resolved_at: null,
    deleted_at: null,
    user_id: null,
    state: null,
    motivo_rechazo: null,
  });

  useEffect(() => {
    if (!!matriculateId) {
      setData(prev => ({
        ...prev,
        matriculate_id: +matriculateId || null,
      }));
    }
  }, [matriculateId]);

  const handleFormValidation = () => {
    let newFormValidation = {};

    let toValidate = [
      ...Array.from(document.getElementsByClassName('form-control')),
    ] as HTMLInputElement[];
    toValidate = toValidate.filter(e => !e.disabled && e.required);

    let missing = false;
    // Validar campos required
    toValidate.forEach(field => {
      if (field.required && !field.disabled && !field.value?.trim()) {
        field.classList.add('is-invalid');

        missing = true;
      } else if (field.inputMode === 'numeric') {
        if (!/^\d+$/.test(field.value)) {
          newFormValidation = {
            ...newFormValidation,
            [field.name]: false,
          };
        }
      } else {
        field.classList.remove('is-invalid');
      }
    });

    if (missing) {
      setFormValidation(newFormValidation);
      alert(
        'Todos los campos marcados con * son obligatorios. ' +
          'Por favor complete los faltantes antes de enviar la solicitud.',
      );
      return false;
    }

    if (data.forma_pago !== 'Efectivo') {
      const field = document.getElementById('numero_tarjeta_cbu');

      if (data.forma_pago === 'Bancaria') {
        if (data.numero_tarjeta_cbu?.length !== 22) {
          field?.classList.add('is-invalid');
          alert(
            'El CBU debe tener 22 dígitos.',
          );
          return false;
        }
      } else if (data.forma_pago === 'Tarjeta') {
        if (data.numero_tarjeta_cbu?.length !== 16) {
          field?.classList.add('is-invalid');
          alert(
            'El número de tarjeta debe tener 16 dígitos.',
          );
          return false;
        }
      }
    }

    return true;
  };

  // Confirmación de envío de Solicitud
  const handleSendForm = () => {
    if (!handleFormValidation()) {
      return;
    }

    setConfirm(
      true,
      '¿Estás seguro que deseas enviar esta solicitud de modificación de forma de pago?',
      'send',
    );
  };

  // Gestor de respuestas de confirmaciones
  const handleConfirmation = (action: string) => {
    if (action === 'send') {
      handleSend();
    }

    setConfirm(false);
  };

  // Envío de solicitudes
  const handleSend = async () => {
    setFormValidation({});

    setLoading(true);

    // Preparamos los inputs menos los archivos
    const formaPago: Partial<FormaPagoRequest> = {};

    if (!formElement.current) return;

    new FormData(formElement.current).forEach((value, key) => {
      // @ts-ignore
      formaPago[key as keyof FormaPagoRequest] = value.toString() as any;
    });

    // Enviamos la petición
    await postFormaPago(formaPago)
      .then(res => {
        if (!res || res.status != 'ok') {
          // Error interno
          console.error('FormaPagoCreate', 'Error al enviar la solicitud');
          console.info(res);
          alert(
            res.message ??
            'Ha ocurrido un error al enviar la solicitud. Por favor intente nuevamente.',
          );
          return;
        }

        alert('La solicitud se envió correctamente', () => {
          history.push('/forma-pago');
        });
      })
      .catch(err => {
        console.error(
          'FormaPagoCreate',
          'Error inesperado al intentar enviar la solicitud',
        );
        console.info(err);
        alert(
          'Ha ocurrido un error al enviar la solicitud. Por favor intente nuevamente.',
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { target } = event;

    const name = target.name;

    const value: string | number | boolean | null = target.value;

    // Guardamos el nuevo valor
    setData({
      ...data,
      [name]: value,
    });
  };

  return (
    <div className="content">
      <Row>
        <Col sm={12}>
          <Card>
            <CardHeader>
              <Row style={{ alignItems: 'center' }}>
                <Col sm={8}>
                  <CardTitle tag="h5">Modificación de Forma de Pago</CardTitle>
                </Col>
                <Col sm={4} className="text-right">
                  <Button
                    className="btn-round"
                    color="primary"
                    onClick={() => {
                      history.push(`/forma-pago`);
                    }}
                  >
                    Volver
                  </Button>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              <Form innerRef={formElement}>
                <Col sm={12}>
                  <CardTitle>Forma de Pago</CardTitle>
                </Col>
                <FormGroup>
                  <Row>
                    <Col md="6">
                      <Label for="forma_pago">
                        Forma de Pago <Required />
                      </Label>
                      <Input
                        id="forma_pago"
                        name="forma_pago"
                        type="select"
                        className="custom-select form-control"
                        value={data.forma_pago ?? ''}
                        invalid={formValidation.forma_pago === false}
                        onChange={handleInputChange}
                        disabled={isLoading}
                        required
                      >
                        <option value="">Seleccione una opción...</option>
                        <option value="Efectivo">Efectivo por Mercado Pago</option>
                        <option value="Bancaria">Débito automático en cuenta bancaria con CBU</option>
                        <option value="Tarjeta">Débito automático en tarjeta de crédito</option>
                      </Input>
                    </Col>
                    <Col md="6"></Col>
                  </Row>
                </FormGroup>
                { data.forma_pago === 'Tarjeta' && (
                  <FormGroup>
                    <Row>
                      <Col md="6">
                        <Label for="tipo_tarjeta">
                          Tarjetas Aceptadas <Required />
                        </Label>
                        <Input
                          id="tipo_tarjeta"
                          name="tipo_tarjeta"
                          type="select"
                          className="custom-select form-control"
                          value={data.tipo_tarjeta ?? ''}
                          invalid={formValidation.tipo_tarjeta === false}
                          onChange={handleInputChange}
                          disabled={isLoading}
                          required
                        >
                          <option value="">Seleccione una opción...</option>
                          <option value='VISA'>VISA</option>
                          <option value='MasterCard'>MasterCard</option>
                        </Input>
                      </Col>
                      <Col md="6">
                        <Label for="numero_tarjeta_cbu">
                          Número de tarjeta de crédito <Required />
                        </Label>
                        <Input
                          id='numero_tarjeta_cbu'
                          name='numero_tarjeta_cbu'
                          type='number'
                          value={data.numero_tarjeta_cbu ?? ''}
                          invalid={formValidation.numero_tarjeta_cbu === false}
                          onChange={handleInputChange}
                          disabled={isLoading}
                          required
                        />
                      </Col>
                    </Row>
                  </FormGroup>
                )}
                { data.forma_pago === 'Bancaria' && (
                  <FormGroup>
                    <Row>
                      <Col md="6">
                        <Label for="numero_tarjeta_cbu">
                          CBU <Required />
                        </Label>
                        <Input
                          id='numero_tarjeta_cbu'
                          name='numero_tarjeta_cbu'
                          type='number'
                          value={data.numero_tarjeta_cbu ?? ''}
                          invalid={formValidation.numero_tarjeta_cbu === false}
                          onChange={handleInputChange}
                          disabled={isLoading}
                          required
                        />
                      </Col>
                    </Row>
                  </FormGroup>
                )}
                <FormGroup>
                  <div className="text-center">
                    <Button
                      type="button"
                      className="btn-round"
                      onClick={handleSendForm}
                      disabled={isLoading}
                    >
                      Solicitar
                    </Button>
                  </div>
                </FormGroup>
              </Form>
              <AlertModal
                isOpen={showAlert}
                message={message}
                onClose={alertCallback}
              />
              <ConfirmModal
                isOpen={confirm}
                title={confirmTitle}
                action={confirmAction}
                onClose={() => setConfirm(false)}
                onConfirm={handleConfirmation}
              />
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
